// Tiny presentational pagination strip. Pairs with usePagination().
//
// Renders nothing when totalCount fits on a single page AND the
// caller hasn't enabled `alwaysShow` (so empty lists / tiny result
// sets don't get noisy chrome). Page-size selector is always
// rendered when the bar shows, so the user can widen the page even
// before pagination is necessary.
import { PAGE_SIZE_OPTIONS, type PageSize } from "./usePagination";

const inputClass =
  "rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";
const navBtnClass =
  "rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800";

export type PaginationBarProps = {
  pageIndex: number;
  pageCount: number;
  pageSize: PageSize;
  totalCount: number;
  firstShown: number;
  lastShown: number;
  onPageChange: (next: number) => void;
  onPageSizeChange: (next: PageSize) => void;
  /** Override the shared 5/10/20/25/30 list (e.g. deals use 5/10/25). */
  pageSizeOptions?: readonly number[];
  /** Optional noun for the "Showing X-Y of N <noun>" label (e.g. "locations"). */
  noun?: string;
  /** Show the bar even when everything fits on one page. */
  alwaysShow?: boolean;
};

export function PaginationBar({
  pageIndex,
  pageCount,
  pageSize,
  totalCount,
  firstShown,
  lastShown,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  noun = "items",
  alwaysShow = false,
}: PaginationBarProps) {
  if (!alwaysShow && totalCount <= pageSize) {
    // Hide the bar entirely if there's nothing to page through and the
    // caller didn't ask for the size selector to be visible.
    return null;
  }
  const atFirst = pageIndex <= 0;
  const atLast = pageIndex + 1 >= pageCount;
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-zinc-400">
      <label className="flex items-center gap-1">
        Show
        <select
          className={inputClass}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value) as PageSize)}
          aria-label="Items per page"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        per page
      </label>

      <div className="ml-auto flex items-center gap-2">
        <span>
          {totalCount === 0
            ? `No ${noun}`
            : `Showing ${firstShown}–${lastShown} of ${totalCount} ${noun}`}
        </span>
        <button
          type="button"
          className={navBtnClass}
          onClick={() => onPageChange(Math.max(0, pageIndex - 1))}
          disabled={atFirst}
          aria-label="Previous page"
        >
          ← Prev
        </button>
        <span aria-live="polite">
          Page {pageIndex + 1} of {pageCount}
        </span>
        <button
          type="button"
          className={navBtnClass}
          onClick={() => onPageChange(Math.min(pageCount - 1, pageIndex + 1))}
          disabled={atLast}
          aria-label="Next page"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
